import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';

export type ReceiptStatus = 'PENDING' | 'PROCESSING' | 'APPROVED' | 'REJECTED' | 'REVIEW';
export type ReviewSource = 'AI_AUTO' | 'MANUAL' | null;

/** Semaforo antifrode — presente solo quando il backend shadow è attivo sul record. */
export type TrafficLight = 'GREEN' | 'YELLOW' | 'RED';

export interface AuthenticitySignal {
  code: string;
  weight: number;
  detail: string;
}

export interface ReceiptDto {
  id: string;
  userId: string;
  userEmail: string | null;
  userDisplayName: string | null;
  imageUrl: string;
  imageDownloadUrl?: string;
  status: ReceiptStatus;
  sponsorId: string | null;
  sponsorName: string | null;
  aiStoreName: string | null;
  aiPartitaIva: string | null;
  aiBusinessName: string | null;
  aiDate: string | null;
  aiTotalAmount: number | null;
  aiReceiptNumber: string | null;
  aiConfidence: number | null;
  rejectionReason: string | null;
  pointsAwarded: number;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewSource: ReviewSource;
  processedAt: string | null;
  createdAt: string;
  // --- Campi antifrode (shadow, opzionali finché non attivi su tutti i record) ---
  /** Semaforo lettura dati derivato da aiConfidence */
  readingLight?: TrafficLight;
  /** Semaforo veridicità derivato dallo score V */
  authenticityLight?: TrafficLight;
  /** Score V di veridicità 0-100 */
  authenticityScore?: number;
  /** R_norm - V; alto = potenziale falso sofisticato */
  disparity?: number;
  /** Segnali di veridicità — presente solo per admin */
  authenticitySignals?: AuthenticitySignal[];
  /** Scadenza SLA 7gg revisione manuale (regolamento §5) — solo se status=REVIEW */
  reviewSlaDueAt: string | null;
  reviewSlaOverdue: boolean;
}

export interface PaginatedReceipts {
  data: ReceiptDto[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export interface ReviewReceiptInput {
  decision: 'APPROVE' | 'REJECT' | 'REVIEW';
  pointsAwarded?: number;
  rejectionReason?: string;
  // Admin override AI fields
  aiStoreName?: string;
  aiPartitaIva?: string;
  aiBusinessName?: string;
  aiDate?: string;
  aiTotalAmount?: number;
  aiReceiptNumber?: string;
  // T-155 — partner scelto a mano dall'admin quando la risoluzione automatica
  // non aggancia nessuno sponsor. Senza partner il server rifiuta l'approvazione.
  sponsorId?: string;
}

interface ReceiptsFilters {
  page?: number;
  limit?: number;
  status?: ReceiptStatus;
  sponsorId?: string;
  search?: string;
}

const QK = {
  list: (filters: ReceiptsFilters) => ['receipts', filters] as const,
  one: (id: string) => ['receipts', id] as const,
};

export function useReceipts(filters: ReceiptsFilters = {}) {
  return useQuery({
    queryKey: QK.list(filters),
    queryFn: async () => {
      const params: Record<string, string | number> = {};
      if (filters.page) params.page = filters.page;
      if (filters.limit) params.limit = filters.limit;
      if (filters.status) params.status = filters.status;
      if (filters.sponsorId) params.sponsorId = filters.sponsorId;
      if (filters.search) params.search = filters.search;
      const res = await apiClient.get<PaginatedReceipts>('/receipts', { params });
      return res.data;
    },
  });
}

export function useReceipt(id: string | null) {
  return useQuery({
    queryKey: QK.one(id ?? ''),
    enabled: id !== null,
    queryFn: async () => {
      const res = await apiClient.get<ReceiptDto>(`/receipts/${id}`);
      return res.data;
    },
  });
}

export interface AdminManualReceiptInput {
  userId: string;
  pointsAwarded: number;
  storeName?: string;
  partitaIva?: string;
  businessName?: string;
  totalAmount?: number;
  date?: string;
  receiptNumber?: string;
  note?: string;
}

export function useAdminManualReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AdminManualReceiptInput) => {
      const res = await apiClient.post<ReceiptDto>('/receipts/admin/manual', input);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['receipts'] });
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useReviewReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: ReviewReceiptInput }) => {
      const res = await apiClient.patch<ReceiptDto>(`/receipts/${id}/review`, input);
      return res.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['receipts'] });
      qc.invalidateQueries({ queryKey: QK.one(vars.id) });
    },
  });
}

/**
 * T-266 — cambia/assegna il partner (Sponsor) di uno scontrino esistente.
 * Non ricalcola i punti e non approva; su uno gia' approvato e' ri-attribuzione.
 */
export function useSetReceiptSponsor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, sponsorId }: { id: string; sponsorId: string }) => {
      const res = await apiClient.patch<ReceiptDto>(`/receipts/${id}/sponsor`, { sponsorId });
      return res.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['receipts'] });
      qc.invalidateQueries({ queryKey: QK.one(vars.id) });
    },
  });
}

export interface ReopenReceiptInput {
  reason: string;
}

/**
 * T-138 — riapertura admin di uno scontrino RESPINTO (REJECTED → REVIEW). NON
 * approva: rimette lo scontrino in coda di revisione. L'approvazione (con i punti)
 * è un secondo passo separato (useReviewReceipt con decision APPROVE).
 */
export function useReopenReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: ReopenReceiptInput }) => {
      const res = await apiClient.patch<ReceiptDto>(`/receipts/${id}/reopen`, input);
      return res.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['receipts'] });
      qc.invalidateQueries({ queryKey: QK.one(vars.id) });
    },
  });
}

export interface PurgeImagesResult {
  count: number;
  deleted: number;
  errors: number;
}

export function usePurgeReceiptImages() {
  return useMutation<PurgeImagesResult, Error, { from: string; to: string; dryRun?: boolean }>({
    mutationFn: async (input) => {
      const res = await apiClient.post<PurgeImagesResult>('/receipts/admin/purge-images', input);
      return res.data;
    },
  });
}
